import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Logger } from "infra/logger/logger";
import attachedDocumentRepository from "../../infra/repositories/attached-document-repository";

export const useAttachedDocuments = (objectType: string, objectId: string | undefined, enabled = true) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["attachedDocuments", objectType, objectId],
    queryFn: async () => {
      if (!objectId) return [];
      try {
        const result = await attachedDocumentRepository.getByObjectId(objectType, objectId);
        return result.data || [];
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata?.errors?.[0] || "Error fetching attached documents",
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata?.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
    enabled: enabled && !!objectId,
  });
};

export const useCreateAttachedDocumentMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const result = await attachedDocumentRepository.create(data);
      return result;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["attachedDocuments", variables.objectType, variables.objectId] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors || ["Error creating document"];
        logger.error(errors[0], error.response?.data?.metadata);
      }
    },
  });
};

export const useDeleteAttachedDocumentMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, objectType, objectId }: { id: string, objectType: string, objectId: string }) => {
      const result = await attachedDocumentRepository.delete(id);
      return result;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["attachedDocuments", variables.objectType, variables.objectId] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors || ["Error deleting document"];
        logger.error(errors[0], error.response?.data?.metadata);
      }
    },
  });
};
